// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

// React auto-imported via JSX transform
import type { NotificationRecord } from '@vn-grc/shared';

interface NotificationItemProps {
  notification: NotificationRecord;
  onClick: (notification: NotificationRecord) => void;
  t: Record<string, any>;
}

const ICON_MAP: Record<string, string> = {
  task: '\u2611',
  alert: '\u26A0',
  approval: '\u2714',
  workflow: '\u21C4',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Individual notification row for the drawer.
 * Sprint 17.3.0: Notification System (Phase 15)
 */
export function NotificationItem({ notification, onClick, t: _t }: NotificationItemProps) {
  const icon = ICON_MAP[notification.icon ?? ''] ?? '\u{1F514}';
  const isUnread = notification.status === 'unread';
  const priorityClass =
    notification.priority === 'critical'
      ? 'notif-priority-critical'
      : notification.priority === 'high'
        ? 'notif-priority-high'
        : '';

  return (
    <button
      className={`notif-item ${isUnread ? 'notif-item-unread' : ''} ${priorityClass}`}
      onClick={() => onClick(notification)}
      aria-label={`${isUnread ? 'Unread: ' : ''}${notification.title}`}
      type="button"
    >
      <span className="notif-item-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="notif-item-content">
        <div className="notif-item-title">{notification.title}</div>
        <div className="notif-item-body">{notification.body}</div>
        <div className="notif-item-time">{timeAgo(notification.created_at)}</div>
      </div>
      {isUnread && <span className="notif-item-dot" aria-hidden="true" />}
    </button>
  );
}
